import { Link, router } from "@inertiajs/react";
import { useEffect } from "react";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle, ArrowLeft, CheckCircle2, Download, Loader2 } from "lucide-react";

type BoozeImport = {
  id: number;
  status: "pending" | "processing" | "completed" | "failed";
  totalRows: number | null;
  importedCount: number;
  failedCount: number;
  completedAt: string | null;
  errorMessage: string | null;
  hasFailureCsv: boolean;
};

type Props = {
  boozeImport: BoozeImport;
  flash?: { notice?: string; alert?: string };
};

export default function BoozeImportsShow({ boozeImport, flash }: Props) {
  useFlashToast(flash);
  const poll =
    boozeImport.status === "pending" || boozeImport.status === "processing";

  useEffect(() => {
    if (!poll) return;
    const t = setInterval(() => {
      router.reload();
    }, 2000);
    return () => clearInterval(t);
  }, [poll]);

  const isRunning = poll;
  const jobFailed = boozeImport.status === "failed";
  const done = boozeImport.status === "completed";
  const partial = done && boozeImport.failedCount > 0;

  const failuresUrl = `/booze_imports/${boozeImport.id}/failures`;

  return (
    <div className="px-4 py-8">
      <div className="mx-auto max-w-xl space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/boozes">
            <ArrowLeft className="size-4" />
            Back to booze
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              {isRunning && (
                <Loader2 className="size-5 animate-spin text-amber-500" />
              )}
              {done && !isRunning && (
                <CheckCircle2 className="size-5 text-emerald-500" />
              )}
              {jobFailed && <AlertCircle className="size-5 text-destructive" />}
              <div>
                <CardTitle>Bottle import</CardTitle>
                <CardDescription>
                  Import #{boozeImport.id}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isRunning && (
              <p className="text-sm text-muted-foreground">
                Processing your file… this page will refresh until the import
                finishes.
              </p>
            )}

            {jobFailed && boozeImport.errorMessage && (
              <div
                role="alert"
                className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm"
              >
                <p className="font-medium text-destructive">Import failed</p>
                <p className="mt-1 text-muted-foreground">
                  {boozeImport.errorMessage}
                </p>
              </div>
            )}

            {partial && (
              <div
                role="status"
                className="space-y-3 rounded-md border border-amber-600/50 bg-amber-950/40 p-4 text-sm"
              >
                <p className="font-medium text-amber-200">
                  Some rows could not be created
                </p>
                <p className="text-muted-foreground">
                  {boozeImport.importedCount} row
                  {boozeImport.importedCount === 1 ? "" : "s"} were imported,{" "}
                  {boozeImport.failedCount} row
                  {boozeImport.failedCount === 1 ? "" : "s"} could not be
                  created. Download the file below, fix the issues, and upload
                  again.
                </p>
                {boozeImport.hasFailureCsv && (
                  <Button type="button" size="sm" variant="secondary" asChild>
                    <a href={failuresUrl} download>
                      <Download className="size-4" />
                      Download failed rows (CSV)
                    </a>
                  </Button>
                )}
              </div>
            )}

            {done && !partial && boozeImport.totalRows != null && (
              <p className="text-sm text-muted-foreground">
                Finished. All {boozeImport.totalRows} row
                {boozeImport.totalRows === 1 ? "" : "s"} were imported
                successfully.
              </p>
            )}

            {done && boozeImport.totalRows != null && (
              <ul className="list-inside list-disc text-sm text-muted-foreground">
                <li>Total rows: {boozeImport.totalRows}</li>
                <li>Imported: {boozeImport.importedCount}</li>
                <li>Not imported: {boozeImport.failedCount}</li>
                {boozeImport.completedAt && (
                  <li>
                    Finished:{" "}
                    {new Date(boozeImport.completedAt).toLocaleString()}
                  </li>
                )}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
